import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '@/store/auth';
import toast from 'react-hot-toast';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const { signIn, isAuthenticated, isLoading, error, clearError } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      const result = await signIn(email, password, mfaCode);
      
      if (result.requiresMfa) {
        setRequiresMfa(true);
        toast.success('Please enter your MFA code');
      } else {
        toast.success('Welcome to Auth-as-a-Service Admin');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Sign in failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black relative overflow-hidden">
      {/* Background geometric elements */}
      <div className="absolute inset-0">
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="h-full w-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3e%3cg fill='none' fill-rule='evenodd'%3e%3cg fill='%23ffffff' fill-opacity='1'%3e%3ccircle cx='7' cy='7' r='1'/%3e%3c/g%3e%3c/g%3e%3c/svg%3e")`,
          }} />
        </div>
        
        {/* Gradient overlays */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-600 opacity-10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-600 opacity-10 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2" />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-cyan-500 opacity-5 rounded-full blur-2xl transform -translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="relative min-h-screen flex flex-col lg:flex-row">
        {/* Left side - Branding */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-8 lg:px-16 py-6 lg:py-12">
          <div className="max-w-lg">
            {/* Logo/Brand section */}
            <div className="mb-8 lg:mb-12">
              <div className="flex items-center justify-center lg:justify-start mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center mr-3 sm:mr-4">
                  <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                  </svg>
                </div>
                <div className="text-center lg:text-left">
                  <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">AuthAsAService</h1>
                  <p className="text-xs sm:text-sm text-gray-400 font-medium">ADMIN PORTAL</p>
                </div>
              </div>
            </div>

            {/* Headline section - Hidden on small screens to save space */}
            <div className="space-y-4 lg:space-y-6 hidden lg:block">
              <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
                Secure.
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
                  Scalable.
                </span>
                <br />
                Simple.
              </h2>
              
              <p className="text-lg text-gray-300 leading-relaxed max-w-md">
                Enterprise-grade authentication infrastructure designed for the modern web.
              </p>

              <div className="grid grid-cols-2 gap-6 pt-8">
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-white">99.9%</div>
                  <div className="text-sm text-gray-400 uppercase tracking-wide">Uptime SLA</div>
                </div>
                <div className="space-y-2">
                  <div className="text-3xl font-bold text-white">10M+</div>
                  <div className="text-sm text-gray-400 uppercase tracking-wide">API Calls</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Login form */}
        <div className="w-full lg:w-480 bg-white bg-opacity-5 backdrop-blur-xl border-t lg:border-t-0 lg:border-l border-white border-opacity-10">
          <div className="h-full flex items-center justify-center px-4 sm:px-8 lg:px-12 py-8 lg:py-12">
            <div className="w-full max-w-sm">
              {/* Form header */}
              <div className="text-center mb-8 lg:mb-10">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                  {requiresMfa ? 'Two-Factor Authentication' : 'Welcome Back'}
                </h3>
                <p className="text-sm sm:text-base text-gray-400">
                  {requiresMfa 
                    ? 'Enter your authenticator code to continue' 
                    : 'Enter your credentials to access the admin portal'
                  }
                </p>
              </div>

              {/* Error message */}
              {error && (
                <div className="mb-6 p-4 bg-red-500 bg-opacity-10 border border-red-500 border-opacity-20 rounded-xl">
                  <div className="text-sm text-red-200">{error}</div>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                {!requiresMfa ? (
                  <>
                    {/* Email field */}
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-gray-200 uppercase tracking-wide">
                        Email Address
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 sm:py-4 bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 focus:border-transparent transition-all duration-200 text-base sm:text-sm min-h-[44px]"
                        placeholder="admin@company.com"
                      />
                    </div>

                    {/* Password field */}
                    <div className="space-y-2">
                      <label htmlFor="password" className="block text-xs sm:text-sm font-semibold text-gray-200 uppercase tracking-wide">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          required
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full px-4 py-3 sm:py-4 bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 focus:border-transparent transition-all duration-200 pr-12 text-base sm:text-sm min-h-[44px]"
                          placeholder="••••••••••••"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-200 transition-colors min-h-[44px] sm:min-h-[auto]"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5 sm:h-5 sm:w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5 sm:h-5 sm:w-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <label htmlFor="mfaCode" className="block text-xs sm:text-sm font-semibold text-gray-200 uppercase tracking-wide">
                      Authenticator Code
                    </label>
                    <input
                      id="mfaCode"
                      name="mfaCode"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      required
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      className="w-full px-4 py-3 sm:py-4 bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 focus:border-transparent transition-all duration-200 text-center text-xl sm:text-2xl tracking-widest min-h-[44px]"
                      placeholder="000000"
                      maxLength={6}
                    />
                    <p className="text-xs text-gray-400 text-center">
                      Enter the 6-digit code from your authenticator app
                    </p>
                  </div>
                )}

                {/* Back button for MFA */}
                {requiresMfa && (
                  <button
                    type="button"
                    onClick={() => {
                      setRequiresMfa(false);
                      setMfaCode('');
                    }}
                    className="text-sm text-blue-400 hover:text-blue-300 transition-colors flex items-center min-h-[44px] sm:min-h-[auto]"
                  >
                    ← Back to login
                  </button>
                )}

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full relative group bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold py-3 sm:py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none min-h-[44px] text-base sm:text-sm"
                >
                  <div className="flex items-center justify-center">
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-opacity-30 border-t-white mr-3"></div>
                        <span className="text-sm sm:text-base">Authenticating...</span>
                      </>
                    ) : (
                      <>
                        <span className="text-sm sm:text-base">{requiresMfa ? 'Verify Code' : 'Sign In'}</span>
                        <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </div>
                </button>
              </form>

              {/* Footer */}
              <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-white border-opacity-10 text-center">
                <p className="text-xs text-gray-500">
                  Protected by enterprise-grade security
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
